import { useCallback, useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  RefreshCw,
  Package,
  Users,
  CreditCard,
  FolderKanban,
  UserCheck,
  UsersRound,
  SlidersHorizontal,
  Sparkles,
  Truck,
  Percent,
  RotateCw,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { quotationIntakeService } from '@features/quotation-intake/services';
import {
  MASTER_DATA_RECORD_TYPES,
  type MasterDataStats,
  type MasterDataRecordType,
} from '@features/quotation-intake/types';
import { configurationService } from '@features/configuration/services';
import type { GeminiConfig } from '@features/configuration/types';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@features/shared/constants/permissions';

type SyncKey =
  | 'items'
  | 'customers'
  | 'terms'
  | 'projectCategories'
  | 'salesReps'
  | 'salesCoordinators'
  | 'brackets'
  | 'truckingRates'
  | 'baseMarkup';

function formatLastSynced(iso: string | null): string {
  if (!iso) return 'Never synced';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface MasterDataConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MasterDataConfigDialog({ open, onOpenChange }: MasterDataConfigDialogProps) {
  const [stats, setStats] = useState<MasterDataStats | null>(null);
  const [termsSyncedAt, setTermsSyncedAt] = useState<string | null>(null);
  const [projectCategoriesSyncedAt, setProjectCategoriesSyncedAt] = useState<string | null>(null);
  const [salesRepsSyncedAt, setSalesRepsSyncedAt] = useState<string | null>(null);
  const [salesCoordinatorsSyncedAt, setSalesCoordinatorsSyncedAt] = useState<string | null>(null);
  const [bracketsSyncedAt, setBracketsSyncedAt] = useState<string | null>(null);
  const [truckingRatesSyncedAt, setTruckingRatesSyncedAt] = useState<string | null>(null);
  const [baseMarkupSyncedAt, setBaseMarkupSyncedAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<SyncKey | null>(null);

  // 86d33u348 — two permissions split the dialog's surfaces:
  //   • canSync gates BOTH the regular Sync buttons AND the Full Sync buttons.
  //     A user without it sees the rows (stats are useful) but no action buttons.
  //   • canEditAi gates the AI Extraction Models Save button + the input fields.
  // Hiding (not disabling) on missing permission so the UI is unambiguous —
  // operators without rights don't wonder why a greyed-out button exists.
  const canSync = usePermission(PERMISSIONS.QUOTATIONS_SYNC_MASTER_DATA);
  const canEditAi = usePermission(PERMISSIONS.QUOTATIONS_EDIT_AI_CONFIG);

  // AI extraction model settings (runtime-editable; backed by Postgres via configurationService).
  const [geminiConfig, setGeminiConfig] = useState<GeminiConfig | null>(null);
  const [primaryModelInput, setPrimaryModelInput] = useState('');
  const [fallbackModelInput, setFallbackModelInput] = useState('');
  const [savingGemini, setSavingGemini] = useState(false);

  const load = useCallback(async () => {
    try {
      setStats(await quotationIntakeService.getMasterDataStats());
    } catch {
      // non-fatal
    }
    try {
      const config = await configurationService.getGeminiConfig();
      setGeminiConfig(config);
      setPrimaryModelInput(config.primaryModel);
      setFallbackModelInput(config.fallbackModel);
    } catch (err) {
      // non-fatal — user can still run syncs even if Gemini config lookup fails
      console.error('Failed to load Gemini config', err);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleSaveGemini = async () => {
    const primary = primaryModelInput.trim();
    const fallback = fallbackModelInput.trim();
    if (!primary || !fallback) {
      toast.error('Both primary and fallback models are required');
      return;
    }
    setSavingGemini(true);
    try {
      const updated = await configurationService.updateGeminiConfig({
        primaryModel: primary,
        fallbackModel: fallback,
      });
      setGeminiConfig(updated);
      setPrimaryModelInput(updated.primaryModel);
      setFallbackModelInput(updated.fallbackModel);
      toast.success('AI extraction models updated');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update AI extraction models'));
    } finally {
      setSavingGemini(false);
    }
  };

  const geminiDirty =
    !!geminiConfig &&
    (primaryModelInput.trim() !== geminiConfig.primaryModel ||
      fallbackModelInput.trim() !== geminiConfig.fallbackModel);

  const runSync = async (
    key: SyncKey,
    label: string,
    fn: () => Promise<void>,
  ) => {
    setSyncing(key);
    try {
      await fn();
      toast.success(`${label} synced`);
      if (key === 'terms') setTermsSyncedAt(new Date().toISOString());
      if (key === 'projectCategories') setProjectCategoriesSyncedAt(new Date().toISOString());
      if (key === 'salesReps') setSalesRepsSyncedAt(new Date().toISOString());
      if (key === 'salesCoordinators') setSalesCoordinatorsSyncedAt(new Date().toISOString());
      if (key === 'brackets') setBracketsSyncedAt(new Date().toISOString());
      if (key === 'truckingRates') setTruckingRatesSyncedAt(new Date().toISOString());
      if (key === 'baseMarkup') setBaseMarkupSyncedAt(new Date().toISOString());
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, `${label} sync failed`));
    } finally {
      setSyncing(null);
    }
  };

  // 86d33u348 — Full Sync runner. Confirms intent before triggering (the operation
  // is non-destructive but expensive — can pull ~9,200 customer rows from NetSuite),
  // then delegates to runSync so the spinner / toast / refresh logic stays uniform
  // with the incremental Sync path.
  const runForceSync = (
    key: SyncKey,
    label: string,
    recordType: MasterDataRecordType,
    rowCountHint: string,
  ) => async () => {
    const confirmed = window.confirm(
      `Full Sync ${label}?\n\n` +
        `This pulls ${rowCountHint} from NetSuite. It's slower than Sync — ` +
        `only use it if you think the data is out of date.`,
    );
    if (!confirmed) return;
    await runSync(key, `${label} (full)`, () => quotationIntakeService.forceSync(recordType));
  };

  const rows: Array<{
    key: SyncKey;
    icon: LucideIcon;
    label: string;
    detail: string;
    /** Regular ("incremental" for customer, basic refresh for others) sync action.
     *  Absent on rows where only Full Sync is exposed (the 3 webhook-fed caches). */
    onSync?: () => Promise<void>;
    /** Force-full sync action. Renders only when the user has
     *  QUOTATIONS_SYNC_MASTER_DATA. Confirmation prompt is fired by runForceSync. */
    onForceSync?: () => Promise<void>;
  }> = [
    {
      key: 'items',
      icon: Package,
      label: 'Rebar Items',
      detail: stats
        ? `${stats.itemCount.toLocaleString()} items · ${formatLastSynced(stats.lastSyncedAt)}`
        : 'Loading…',
      onSync: () =>
        runSync('items', 'Rebar items', async () => {
          await quotationIntakeService.syncRebarItems();
        }),
    },
    {
      key: 'customers',
      icon: Users,
      label: 'Customers',
      detail: stats
        ? `${stats.customerCount.toLocaleString()} customers · ${formatLastSynced(stats.customersLastSyncedAt)}`
        : 'Loading…',
      // Incremental (watermark-driven) sync — 86d33u348. Backend method:
      // SyncCustomersIncrementalAsync. Picks up only customers edited since last
      // sync, typically a handful of rows.
      onSync: () =>
        runSync('customers', 'Customers', async () => {
          await quotationIntakeService.syncCustomers();
        }),
      // Force-full — pulls the entire customer base (~9,200 rows). For drift
      // recovery / schema migration only.
      onForceSync: runForceSync(
        'customers',
        'Customers',
        MASTER_DATA_RECORD_TYPES.Customers,
        '~9,200 customer rows',
      ),
    },
    {
      key: 'terms',
      icon: CreditCard,
      label: 'Payment Terms',
      detail: termsSyncedAt
        ? `Refreshed ${formatLastSynced(termsSyncedAt)}`
        : 'In-memory cache',
      onSync: () =>
        runSync('terms', 'Payment terms', async () => {
          await quotationIntakeService.syncTerms();
        }),
    },
    {
      key: 'projectCategories',
      icon: FolderKanban,
      label: 'Project Categories',
      detail: projectCategoriesSyncedAt
        ? `Refreshed ${formatLastSynced(projectCategoriesSyncedAt)}`
        : 'In-memory cache',
      onSync: () =>
        runSync('projectCategories', 'Project categories', async () => {
          await quotationIntakeService.syncProjectCategories();
        }),
    },
    {
      key: 'salesReps',
      icon: UserCheck,
      label: 'Sales Representatives',
      detail: salesRepsSyncedAt
        ? `Refreshed ${formatLastSynced(salesRepsSyncedAt)}`
        : 'In-memory cache',
      onSync: () =>
        runSync('salesReps', 'Sales representatives', async () => {
          await quotationIntakeService.syncSalesReps();
        }),
    },
    {
      key: 'salesCoordinators',
      icon: UsersRound,
      label: 'Sales Coordinators',
      detail: salesCoordinatorsSyncedAt
        ? `Refreshed ${formatLastSynced(salesCoordinatorsSyncedAt)}`
        : 'In-memory cache',
      onSync: () =>
        runSync('salesCoordinators', 'Sales coordinators', async () => {
          await quotationIntakeService.syncSalesCoordinators();
        }),
    },
    {
      key: 'brackets',
      icon: SlidersHorizontal,
      label: 'Markup % Brackets',
      detail: bracketsSyncedAt
        ? `Refreshed ${formatLastSynced(bracketsSyncedAt)}`
        : 'In-memory cache · webhook-fed',
      // 86d33u348 — webhook-fed cache: no "incremental" variant exists. The hourly
      // periodic worker + reconciliation worker keep it fresh automatically. Full
      // Sync remains as the manual escape hatch for drift recovery.
      onForceSync: runForceSync(
        'brackets',
        'Markup brackets',
        MASTER_DATA_RECORD_TYPES.Brackets,
        'all markup bracket rows',
      ),
    },
    {
      key: 'truckingRates',
      icon: Truck,
      label: 'Trucking Rates',
      detail: truckingRatesSyncedAt
        ? `Refreshed ${formatLastSynced(truckingRatesSyncedAt)}`
        : 'In-memory cache · webhook-fed',
      // 86d33u348 — webhook-fed cache. See Brackets comment above.
      onForceSync: runForceSync(
        'truckingRates',
        'Trucking rates',
        MASTER_DATA_RECORD_TYPES.TruckingRates,
        'all freight location rows',
      ),
    },
    {
      key: 'baseMarkup',
      icon: Percent,
      label: 'Base Markup',
      detail: baseMarkupSyncedAt
        ? `Refreshed ${formatLastSynced(baseMarkupSyncedAt)}`
        : 'In-memory cache · webhook-fed',
      // 86d33u348 — webhook-fed cache. See Brackets comment above.
      onForceSync: runForceSync(
        'baseMarkup',
        'Base markup',
        MASTER_DATA_RECORD_TYPES.BaseMarkupValues,
        'the latest base-markup record',
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Master Data Configuration</DialogTitle>
          <DialogDescription>
            Sync each master data source independently from NetSuite and configure AI extraction models.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {rows.map(({ key, icon: Icon, label, detail, onSync, onForceSync }) => {
            const isBusy = syncing === key;
            const isDisabled = syncing !== null;
            // 86d33u348 — both Sync and Full Sync require quotations.sync_master_data.
            // Stats stay visible regardless; only the action buttons are gated.
            const showSync = onSync != null && canSync;
            const showForceSync = onForceSync != null && canSync;
            return (
              <div
                key={key}
                className="grid grid-cols-12 items-center gap-3 rounded-md border bg-muted/30 px-3 py-2"
              >
                {/* col-span allocation across 12-col grid:
                 *   icon  : col-span-1  (narrow visual anchor)
                 *   label : col-span-7  (wide; truncates on overflow)
                 *   buttons: col-span-4 (right-aligned flex; fits 0 / 1 / 2 buttons cleanly) */}
                <Icon className="col-span-1 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="col-span-7 min-w-0">
                  <div className="text-sm font-medium">{label}</div>
                  <div className="truncate text-xs text-muted-foreground">{detail}</div>
                </div>
                <div className="col-span-4 flex items-center justify-end gap-2">
                  {showSync && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onSync}
                      disabled={isDisabled}
                      className="shrink-0"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isBusy ? 'animate-spin' : ''}`} />
                      {isBusy ? 'Syncing…' : 'Sync'}
                    </Button>
                  )}
                  {showForceSync && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={onForceSync}
                      disabled={isDisabled}
                      className="shrink-0"
                      title="Re-pulls every record from NetSuite. Slower than Sync — use if data looks stale."
                    >
                      <RotateCw className={`h-3.5 w-3.5 mr-1.5 ${isBusy ? 'animate-spin' : ''}`} />
                      {isBusy ? 'Syncing…' : 'Full Sync'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-2 space-y-3 rounded-md border bg-muted/30 px-3 py-3">
          {/* Same 12-col grid as the sync rows above so the icon, title, and
           *  right-zone (timestamp here, buttons there) align vertically across
           *  the dialog. */}
          <div className="grid grid-cols-12 items-center gap-3">
            <Sparkles className="col-span-1 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="col-span-7 text-sm font-medium">AI Extraction Models</div>
            {geminiConfig && (
              <div className="col-span-4 truncate text-right text-xs text-muted-foreground">
                Updated {formatLastSynced(geminiConfig.updatedAt)}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Quotation extraction tries the primary model first. On failure it retries once with the fallback.
            Changes apply immediately — no restart needed.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="gemini-primary" className="text-xs">Primary model</Label>
              <Input
                id="gemini-primary"
                value={primaryModelInput}
                onChange={(e) => setPrimaryModelInput(e.target.value)}
                placeholder="gemini-2.5-flash"
                spellCheck={false}
                // 86d33u348 — read-only for users without quotations.edit_ai_config so
                // they can SEE the current models but can't change them.
                disabled={!geminiConfig || savingGemini || !canEditAi}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="gemini-fallback" className="text-xs">Fallback model</Label>
              <Input
                id="gemini-fallback"
                value={fallbackModelInput}
                onChange={(e) => setFallbackModelInput(e.target.value)}
                placeholder="gemini-3.1-flash-live-preview"
                spellCheck={false}
                disabled={!geminiConfig || savingGemini || !canEditAi}
              />
            </div>
          </div>
          {canEditAi && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleSaveGemini}
                disabled={!geminiConfig || savingGemini || !geminiDirty}
              >
                {savingGemini ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
